// ============================================
// LOGIN PAGE - WITH TURNSTILE + i18n
// ============================================

import React from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, AlertCircle, Eye, EyeOff, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLogin, useGoogleLogin } from '@/hooks';
import { useAuthStore } from '@/store/auth';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from '@crew-at-sea/i18n';
import { authApi } from '@/services/api';

declare global {
  interface Window {
    google?: any;
  }
}

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

// i18n destekli schema factory
function createLoginSchema(t: (key: string) => string) {
  return z.object({
    email: z.string().email(t('auth:validation.invalidEmail')),
    password: z.string().min(6, t('auth:validation.passwordMin6')),
  });
}

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

function LoginPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const loginMutation = useLogin();
  const googleLoginMutation = useGoogleLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const [googleCredential, setGoogleCredential] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const isRoleSelectionError = googleLoginMutation.isError && 
    (googleLoginMutation.error instanceof Error && 
     (googleLoginMutation.error.message.includes('rol seçilmesi') || googleLoginMutation.error.message.includes('role')));

  const handleGoogleCallback = (response: any) => {
    googleLoginMutation.mutate(
      { credential: response.credential },
      {
        onError: (error: any) => {
          const errMsg = error?.message || '';
          if (error?.status === 400 || errMsg.includes('rol seçilmesi')) {
            setGoogleCredential(response.credential);
            googleLoginMutation.reset();
            setShowRoleModal(true);
          }
        },
      }
    );
  };

  const handleGoogleLoginWithRole = (selectedRole: 'SEAFARER' | 'COMPANY') => {
    if (!googleCredential) return;
    googleLoginMutation.mutate(
      { credential: googleCredential, role: selectedRole },
      {
        onSuccess: () => {
          setShowRoleModal(false);
          setGoogleCredential(null);
          googleLoginMutation.reset();
        },
      }
    );
  };

  useEffect(() => {
    let checkGoogle: any;
    
    const initializeGoogleSignIn = () => {
      if (typeof window !== 'undefined' && window.google) {
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '814477697426-tl8102p2a1hcqtglaikrkkvfrn5gpago.apps.googleusercontent.com',
          callback: handleGoogleCallback,
        });
        window.google.accounts.id.renderButton(
          document.getElementById('googleBtn'),
          { theme: 'outline', size: 'large', width: '100%' }
        );
      }
    };

    if (window.google) {
      initializeGoogleSignIn();
    } else {
      checkGoogle = setInterval(() => {
        if (window.google) {
          initializeGoogleSignIn();
          clearInterval(checkGoogle);
        }
      }, 500);
    }

    return () => {
      if (checkGoogle) clearInterval(checkGoogle);
    };
  }, []);

  const loginSchema = useMemo(() => createLoginSchema(t), [t]);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const handleResendVerification = async () => {
    const email = getValues('email');
    if (!email) {
      toast.error('Lütfen önce e-posta adresinizi girin.');
      return;
    }
    
    setIsResending(true);
    try {
      const response = await authApi.resendVerification({ email });
      const msg = response.message || 'Doğrulama e-postası başarıyla gönderildi.';
      toast.success(msg);
    } catch (err: any) {
      toast.error(err?.message || 'Doğrulama e-postası gönderilemedi.');
    } finally {
      setIsResending(false);
    }
  };

  // Kimlik doğrulanmışsa yönlendir
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/' });
    }
  }, [isAuthenticated, navigate]);

  // Honeypot state — bot tespiti
  const honeypotRef = React.useRef<HTMLInputElement>(null);

  const onSubmit = (data: LoginFormData) => {
    // Honeypot kontrolü — bot tespiti
    if (honeypotRef.current?.value) return;
    loginMutation.mutate(data);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{
        backgroundImage: `url('/login-bg.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Mavi overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-maritime-900/85 to-maritime-700/80" />
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
            <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10 brightness-0 invert" />
          </div>
          <h1 className="text-3xl font-bold text-white">Crew At Sea</h1>
          <p className="text-maritime-200 mt-1">{t('panelSubtitle.user')}</p>
        </div>

        {/* Giriş Formu */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('panelLoginTitle.user')}</h2>

          {/* Hata Mesajı */}
          {(loginMutation.isError || (googleLoginMutation.isError && !isRoleSelectionError)) && (
            <div className="mb-4 p-4 rounded-2xl bg-red-50/60 border border-red-100 backdrop-blur-sm shadow-sm flex flex-col gap-3 text-red-800 text-sm animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="font-medium leading-relaxed">
                  {loginMutation.isError
                    ? (loginMutation.error instanceof Error ? loginMutation.error.message : t('login.errorDefault'))
                    : (googleLoginMutation.error instanceof Error ? googleLoginMutation.error.message : t('login.errorDefault'))}
                </span>
              </div>
            </div>
          )}

          {/* Doğrulama Tekrar Gönder Butonu (Kartın Dışında ve Ortalanmış) */}
          {loginMutation.isError &&
            loginMutation.error instanceof Error &&
            loginMutation.error.message.includes('doğrulamanızın') && (
              <div className="flex justify-center w-full mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 hover:text-primary-900 border border-primary-100 transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                  disabled={isResending}
                >
                  {isResending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Mail className="w-3.5 h-3.5" />
                  )}
                  {isResending ? 'Gönderiliyor...' : 'Doğrulama e-postasını tekrar gönder'}
                </button>
              </div>
            )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* E-posta */}
            <div>
              <label htmlFor="email" className="label">
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                className={`input ${errors.email ? 'input-error' : ''}`}
                placeholder={t('login.emailPlaceholder')}
                {...register('email')}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* Şifre */}
            <div>
              <label htmlFor="password" className="label">
                {t('login.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`input pr-10 ${errors.password ? 'input-error' : ''}`}
                  placeholder={t('login.passwordPlaceholder')}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>
              )}
            </div>

            {/* Şifremi Unuttum */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-maritime-600 hover:text-maritime-700"
              >
                {t('login.forgotPassword')}
              </Link>
            </div>

            {/* Gönder Butonu */}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('login.submitting')}
                </>
              ) : (
                t('login.submit')
              )}
            </button>
            {/* Honeypot — botlar için gizli alan, kullanıcı görmez */}
            <input
              ref={honeypotRef}
              type="text"
              name="website"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
            />
          </form>

          {/* Google ile Giriş */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('login.or') || 'veya'}</span>
            </div>
          </div>

          <div className="w-full flex justify-center min-h-[44px]">
            {googleLoginMutation.isPending ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin text-maritime-600" />
                Giriş yapılıyor...
              </div>
            ) : (
              <>
                <style>{`
                  #googleBtn {
                    display: flex !important;
                    justify-content: center !important;
                    width: 100% !important;
                  }
                  #googleBtn > div {
                    margin: 0 auto !important;
                  }
                `}</style>
                <div id="googleBtn" className="w-full"></div>
              </>
            )}
          </div>

          {/* Kayıt Linki */}
          <p className="mt-6 text-center text-sm text-gray-500">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-maritime-600 hover:text-maritime-700 font-medium">
              {t('login.registerLink')}
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-maritime-200 text-sm mt-8">
          {t('footer.copyright', { year: new Date().getFullYear() })}
        </p>
      </div>

      {/* Google Role Selection Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => {
              if (!googleLoginMutation.isPending) {
                setShowRoleModal(false);
                setGoogleCredential(null);
                googleLoginMutation.reset();
              }
            }}
          />

          {/* Modal Content */}
          <div className="relative w-full max-w-md bg-white rounded-3xl border border-slate-100 shadow-2xl overflow-hidden z-10 flex flex-col p-6 md:p-8 animate-in fade-in zoom-in-95 duration-200">
            <div className="text-center space-y-6 py-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                <Loader2 className={`w-6 h-6 ${googleLoginMutation.isPending ? 'animate-spin' : 'animate-pulse'}`} />
              </div>
              
              <div>
                <h2 className="text-xl font-bold text-slate-900">Hesap Türünü Seçin</h2>
                <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                  Google ile ilk defa giriş yapıyorsunuz. Devam etmek için bir rol seçin.
                </p>
              </div>

              {googleLoginMutation.isError && !isRoleSelectionError && (
                <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex gap-3 text-red-700 text-sm text-left">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>
                    {googleLoginMutation.error instanceof Error 
                      ? googleLoginMutation.error.message 
                      : t('login.errorDefault')}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleGoogleLoginWithRole('SEAFARER')}
                  disabled={googleLoginMutation.isPending}
                  className="p-5 rounded-2xl bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50/10 text-center transition-all duration-300 flex flex-col items-center gap-2 group shadow-sm active:scale-95 disabled:opacity-50"
                >
                  <div className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform flex items-center justify-center text-lg">⚓</div>
                  <span className="font-bold text-slate-800 text-sm">Aday (Denizci)</span>
                  <span className="text-slate-400 text-[10px] leading-relaxed">İş arayan denizciler için</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleGoogleLoginWithRole('COMPANY')}
                  disabled={googleLoginMutation.isPending}
                  className="p-5 rounded-2xl bg-white border-2 border-slate-100 hover:border-teal-500 hover:bg-teal-50/10 text-center transition-all duration-300 flex flex-col items-center gap-2 group shadow-sm active:scale-95 disabled:opacity-50"
                >
                  <div className="w-6 h-6 text-teal-600 group-hover:scale-110 transition-transform flex items-center justify-center text-lg">🏢</div>
                  <span className="font-bold text-slate-800 text-sm">İşveren (Şirket)</span>
                  <span className="text-slate-400 text-[10px] leading-relaxed">Personel arayan şirketler için</span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowRoleModal(false);
                  setGoogleCredential(null);
                  googleLoginMutation.reset();
                }}
                disabled={googleLoginMutation.isPending}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors"
              >
                Vazgeç ve Giriş Ekranına Dön
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
